<template>
  <v-chart :option="option" autoresize class="chart-full" />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import VChart from 'vue-echarts';
import { useChartTheme } from '@/composables/useChartTheme';

const props = defineProps<{
  odometerKm: number;
  measuredSoh: number;
  projectedKm: number;
  labelTrajectory: string;
  labelThisVehicle: string;
  labelWarrantyFloor: string;
}>();

const option = computed(() => {
  const C = useChartTheme();

  const totalKm = props.odometerKm + props.projectedKm;
  const maxKm = Math.ceil(totalKm / 50000) * 50000;
  const floorData: [number, number][] = [[0, 70], [maxKm, 70]];
  const trajectoryData: [number, number][] = [[0, 100], [totalKm, 70]];
  const odomKmRounded = Math.round(props.odometerKm);

  return {
    backgroundColor: C.paper,
    grid: { top: 24, bottom: 64, left: 52, right: 24 },
    tooltip: {
      trigger: 'axis',
      formatter: (params: any[]) => {
        const km = params[0]?.axisValue;
        const label = km >= 1000 ? `${Math.round(km / 1000)}k km` : `${km} km`;
        const lines = params
          .filter((p: any) => p.seriesName !== props.labelWarrantyFloor)
          .map((p: any) => `${p.marker} ${p.seriesName}: <strong>${Number(p.value[1]).toFixed(1)}%</strong>`)
          .join('<br/>');
        return `${label}<br/>${lines}`;
      },
    },
    xAxis: {
      type: 'value',
      min: 0,
      max: maxKm,
      axisLabel: {
        color: C.muted,
        fontSize: 11,
        fontFamily: 'Inter',
        formatter: (v: number) => v === 0 ? '0' : `${Math.round(v / 1000)}k`,
      },
      axisLine: { lineStyle: { color: C.line } },
      axisTick: { show: false },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      min: 65,
      max: 101,
      axisLabel: {
        color: C.muted,
        fontSize: 11,
        fontFamily: 'Inter',
        formatter: '{value}%',
      },
      splitLine: { lineStyle: { color: C.line } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    legend: {
      bottom: 0,
      left: 'center',
      orient: 'horizontal',
      itemWidth: 18,
      itemHeight: 8,
      itemGap: 24,
      textStyle: { color: C.muted, fontSize: 11, fontFamily: 'Inter' },
      data: [
        { name: props.labelTrajectory, icon: 'path://M0 3 H18 V5 H0 Z' },
        { name: props.labelThisVehicle, icon: 'circle' },
        { name: props.labelWarrantyFloor, icon: 'path://M0 3 H4 V5 H0 Z M7 3 H11 V5 H7 Z M14 3 H18 V5 H14 Z' },
      ],
    },
    series: [
      {
        name: props.labelTrajectory,
        type: 'line',
        data: trajectoryData,
        symbol: 'none',
        lineStyle: { color: C.accent, width: 2 },
        itemStyle: { color: C.accent },
      },
      {
        name: props.labelThisVehicle,
        type: 'scatter',
        data: [[odomKmRounded, props.measuredSoh]],
        symbolSize: 12,
        z: 5,
        itemStyle: { color: C.good, borderColor: '#fff', borderWidth: 2 },
        label: {
          show: true,
          formatter: `${props.measuredSoh}%  (${odomKmRounded.toLocaleString()} km)`,
          position: 'top',
          color: C.good,
          fontSize: 11,
          fontWeight: 700,
          fontFamily: 'Inter',
        },
      },
      {
        name: props.labelWarrantyFloor,
        type: 'line',
        data: floorData,
        symbol: 'none',
        lineStyle: { color: C.bad, type: 'dashed', width: 1.5 },
        itemStyle: { color: C.bad },
        z: 4,
      },
    ],
  };
});
</script>
